// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Trash2, Edit, Plus, MessageSquare, Calendar, Globe, AlertCircle, CheckCircle, Info, AlertTriangle, Megaphone } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useSportsHubMessages, SportsHubMessage } from '@/hooks/useSportsHubMessages';
import { toast } from 'sonner';

interface Sport {
  id: string;
  name: string;
  gender: string | null;
  active: boolean;
}

const SportsHubMessagesManagement: React.FC = () => {
  const { messages, loading, createMessage, updateMessage, deleteMessage } = useSportsHubMessages();
  const [sports, setSports] = useState<Sport[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<SportsHubMessage | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    message_type: 'info' as 'info' | 'warning' | 'success' | 'error' | 'announcement',
    sport_id: '',
    is_global: false,
    is_active: true,
    display_order: 0,
    starts_at: '',
    expires_at: '',
  });

  useEffect(() => {
    fetchSports();
  }, []);

  const fetchSports = async () => {
    try {
      const { data, error } = await supabase
        .from('sports')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setSports(data || []);
    } catch (error) {
      console.error('Error fetching sports:', error);
      toast.error('Failed to load sports');
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.title.trim() || !formData.message.trim()) {
        toast.error('Title and message are required');
        return;
      }

      const messageData = {
        ...formData,
        sport_id: formData.is_global ? null : (formData.sport_id || null),
        starts_at: formData.starts_at || null,
        expires_at: formData.expires_at || null,
      };

      if (isEditing && selectedMessage) {
        await updateMessage(selectedMessage.id, messageData);
      } else {
        await createMessage(messageData);
      }

      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving message:', error);
    }
  };

  const handleEdit = (message: SportsHubMessage) => {
    setSelectedMessage(message);
    setIsEditing(true);
    setFormData({
      title: message.title,
      message: message.message,
      message_type: message.message_type,
      sport_id: message.sport_id || '',
      is_global: message.is_global,
      is_active: message.is_active,
      display_order: message.display_order,
      starts_at: message.starts_at ? new Date(message.starts_at).toISOString().slice(0, 16) : '',
      expires_at: message.expires_at ? new Date(message.expires_at).toISOString().slice(0, 16) : '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (messageId: string) => {
    if (!confirm('Are you sure you want to delete this message?')) return;
    await deleteMessage(messageId);
  };

  const resetForm = () => {
    setSelectedMessage(null);
    setIsEditing(false);
    setFormData({
      title: '',
      message: '',
      message_type: 'info',
      sport_id: '',
      is_global: false,
      is_active: true,
      display_order: 0,
      starts_at: '',
      expires_at: '',
    });
  };

  const getMessageTypeIcon = (type: string) => {
    switch (type) {
      case 'info':
        return <Info className="w-4 h-4" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4" />;
      case 'success':
        return <CheckCircle className="w-4 h-4" />;
      case 'error':
        return <AlertCircle className="w-4 h-4" />;
      case 'announcement':
        return <Megaphone className="w-4 h-4" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getMessageTypeColor = (type: string) => {
    switch (type) {
      case 'info':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'success':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'announcement':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-lg">Loading sports hub messages...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Sports Hub Messages</h2>
          <p className="text-muted-foreground">
            Create and manage messages that appear on sports hub pages
          </p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              Add Message
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {isEditing ? 'Edit Message' : 'Create Message'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Message title"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message_type">Message Type</Label>
                  <Select 
                    value={formData.message_type} 
                    onValueChange={(value: any) => setFormData({...formData, message_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Info</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="success">Success</SelectItem>
                      <SelectItem value="error">Error</SelectItem>
                      <SelectItem value="announcement">Announcement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Message content"
                  rows={4}
                />
              </div>

              {/* Targeting */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.is_global}
                    onCheckedChange={(checked) => setFormData({...formData, is_global: checked, sport_id: checked ? '' : formData.sport_id})}
                  />
                  <Label>Global Message (appears on all sports hubs)</Label>
                </div>

                {!formData.is_global && (
                  <div className="space-y-2">
                    <Label htmlFor="sport">Target Sport</Label>
                    <Select 
                      value={formData.sport_id} 
                      onValueChange={(value) => setFormData({...formData, sport_id: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a sport" />
                      </SelectTrigger>
                      <SelectContent>
                        {sports.map((sport) => (
                          <SelectItem key={sport.id} value={sport.id}>
                            {sport.name} {sport.gender && `(${sport.gender})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="display_order">Display Order</Label>
                  <Input
                    id="display_order"
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
                  />
                </div>
                
                <div className="flex items-center space-x-2 pt-6">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  />
                  <Label>Active</Label>
                </div>
              </div>

              {/* Scheduling */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="starts_at">Start Date (optional)</Label>
                  <Input
                    id="starts_at"
                    type="datetime-local"
                    value={formData.starts_at}
                    onChange={(e) => setFormData({...formData, starts_at: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="expires_at">End Date (optional)</Label>
                  <Input
                    id="expires_at"
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>
                  {isEditing ? 'Update' : 'Create'} Message
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Messages List */}
      <div className="grid gap-4">
        {messages.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Messages</h3>
              <p className="text-muted-foreground">Create your first sports hub message to get started.</p>
            </CardContent>
          </Card>
        ) : (
          messages.map((message) => (
            <Card key={message.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={`px-2 py-1 ${getMessageTypeColor(message.message_type)}`}>
                        {getMessageTypeIcon(message.message_type)}
                        <span className="ml-1 capitalize">{message.message_type}</span>
                      </Badge>
                      
                      {message.is_global ? (
                        <Badge variant="outline" className="px-2 py-1">
                          <Globe className="w-3 h-3 mr-1" />
                          Global
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="px-2 py-1">
                          {message.sport?.name} {message.sport?.gender && `(${message.sport.gender})`}
                        </Badge>
                      )}
                      
                      {!message.is_active && (
                        <Badge variant="destructive" className="px-2 py-1">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    
                    <h3 className="text-lg font-semibold mb-2">{message.title}</h3>
                    <p className="text-muted-foreground mb-3">{message.message}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {message.starts_at && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Starts: {new Date(message.starts_at).toLocaleString()}
                        </div>
                      )}
                      {message.expires_at && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Expires: {new Date(message.expires_at).toLocaleString()}
                        </div>
                      )}
                      <div>Order: {message.display_order}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(message)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(message.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default SportsHubMessagesManagement;